import React from 'react';
import {
  AdvancedSearchButton,
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  Input,
  InputCheckBox,
  RiDownload2Line
} from '@itandi/itandi-bb-ui';
import { FormProvider, useForm } from 'react-hook-form';
import type { DealSearchCondition } from '~/models/Deal';
import { searchDetailBadge } from '~/models/Deal';
import Styles from './DealSearch.module.scss';
import { useDebounce } from '~/hooks/useDebounce';
import { DealSearchDetailTab } from './DealSearchDetailTab';
import { MaintenanceCompanyCombobox } from '~/components/specifics/vendors/deals/index/search/detail/MaintenanceCompanyCombobox';
import { useDealCompanySettings } from '~/hooks/vendors/useDealCompanySettings';
import { renderSearchButton } from '~/components/common/parts/search/SearchButton';
import { requestCsvGenerate } from '~/utils/api/vendors/CsvGenerateResult';
import DealsCsvExportModal from '~/components/common/dealsCsvExports/DealsCsvExportModal';
import { useSessions } from '~/hooks/vendors/useSessions';

type Props = {
  dealSearchCondition: DealSearchCondition;
  handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
  isSp: boolean;
};

export const DealSearch: React.FC<Props> = ({
  dealSearchCondition,
  handleChangeCondition,
  isSp
}: Props) => {
  const { data: session } = useSessions().index();
  const methods = useForm<DealSearchCondition>({
    mode: 'onChange',
    defaultValues: dealSearchCondition
  });
  const [propertyDealSearchValues, setPropertyDealSearchValues] =
    React.useState({
      name: dealSearchCondition.name ?? '',
      roomNumber: dealSearchCondition.roomNumber ?? ''
    });
  const [searchCompanyName, setSearchCompanyName] = React.useState<string>('');
  const { data: maintenanceCompanies } =
    useDealCompanySettings().index(searchCompanyName);
  const [isOpenSearchDetailTab, setIsOpenSearchDetailTab] =
    React.useState(false);
  const [selectedCompanyMasterId, setSelectedCompanyMasterId] = React.useState(
    dealSearchCondition.companyMasterId
  );
  const handleOpenSearchDetailTab = React.useCallback((): void => {
    setIsOpenSearchDetailTab(true);
  }, []);
  const handleCloseSearchDetailTab = React.useCallback((): void => {
    setIsOpenSearchDetailTab(false);
  }, []);
  const debounce = useDebounce(200);
  const createOnChange = React.useCallback(
    (field: keyof Pick<DealSearchCondition, 'name' | 'roomNumber'>) =>
      async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newValue = e.target.value;
        const newCondition: DealSearchCondition = {
          ...dealSearchCondition,
          [field]: newValue
        };
        setPropertyDealSearchValues((prev) => ({ ...prev, [field]: newValue }));
        methods.setValue(field, newValue);
        debounce(() => {
          handleChangeCondition(newCondition).then();
        });
      },
    [dealSearchCondition, debounce, handleChangeCondition, methods]
  );
  const onChangeName = createOnChange('name');
  const onChangeRoomNumber = createOnChange('roomNumber');
  const handleChangeMaintenanceCompanyInput = React.useCallback(
    (inputText: string): void => {
      debounce(() => {
        setSearchCompanyName(inputText);
      });
    },
    [debounce]
  );
  const onChangeCompanyMasterId = React.useCallback(
    async (companyMasterId: number | undefined): Promise<void> => {
      setSelectedCompanyMasterId(companyMasterId);
      const newCondition: DealSearchCondition = {
        ...dealSearchCondition,
        companyMasterId
      };
      methods.setValue('companyMasterId', companyMasterId);
      await handleChangeCondition(newCondition);
    },
    [dealSearchCondition, handleChangeCondition, methods]
  );
  const handleDetailSearch = React.useCallback(async (): Promise<void> => {
    const newCondition = methods.getValues();
    // 簡易検索の入力欄に詳細検索で入力した値を適応する
    setPropertyDealSearchValues({
      name: newCondition.name ?? '',
      roomNumber: newCondition.roomNumber ?? ''
    });
    setSelectedCompanyMasterId(newCondition.companyMasterId);
    handleCloseSearchDetailTab();
    await handleChangeCondition(newCondition);
  }, [methods, handleCloseSearchDetailTab, handleChangeCondition]);
  const [csvExportModalOpen, setCsvExportModalOpen] = React.useState(false);
  const handleOpenCsvExport = React.useCallback(
    () => setCsvExportModalOpen(true),
    []
  );
  const handleCloseCsvExport = React.useCallback(
    () => setCsvExportModalOpen(false),
    []
  );
  return (
    <div className={Styles.SimpleSearchContainer}>
      <FormProvider {...methods}>
        {isSp ? (
          <>
            <Grid gap={8}>
              <Input
                onChange={onChangeName}
                placeholder="物件名"
                type="search"
                value={propertyDealSearchValues.name}
              />
              <Input
                onChange={onChangeRoomNumber}
                placeholder="部屋番号"
                type="search"
                value={propertyDealSearchValues.roomNumber}
              />
            </Grid>
            <Flex justifyContent="flex-end" marginTop={8}>
              <AdvancedSearchButton
                badge={searchDetailBadge(dealSearchCondition)}
                onClick={handleOpenSearchDetailTab}
              />
            </Flex>
          </>
        ) : (
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              marginBottom={8}
            >
              <Box>
                <Grid
                  alignItems="center"
                  autoFlow="column"
                  gap={8}
                  justifyContent="start"
                >
                  <Box width={240}>
                    <Input
                      onChange={onChangeName}
                      placeholder="物件名"
                      type="search"
                      value={propertyDealSearchValues.name}
                    />
                  </Box>
                  <Box width={120}>
                    <Input
                      onChange={onChangeRoomNumber}
                      placeholder="部屋番号"
                      type="search"
                      value={propertyDealSearchValues.roomNumber}
                    />
                  </Box>
                  <Box width={180}>
                    <MaintenanceCompanyCombobox
                      handleChangeMaintenanceCompanyInput={
                        handleChangeMaintenanceCompanyInput
                      }
                      maintenanceCompanies={maintenanceCompanies || null}
                      onChangeCompanyMasterId={onChangeCompanyMasterId}
                      selectedCompanyMasterId={selectedCompanyMasterId}
                    />
                  </Box>
                  <Box>
                    <AdvancedSearchButton
                      badge={searchDetailBadge(dealSearchCondition)}
                      onClick={handleOpenSearchDetailTab}
                    />
                  </Box>
                  <Box>
                    <InputCheckBox
                      checked={methods.getValues('prioritySort')}
                      label="未読を優先表示する"
                      onChange={(e): void => {
                        const newCondition: DealSearchCondition = {
                          ...dealSearchCondition,
                          prioritySort: e.target.checked
                        };
                        methods.setValue('prioritySort', e.target.checked);
                        handleChangeCondition(newCondition).then();
                      }}
                    />
                  </Box>
                </Grid>
              </Box>
              {session && !session.admin && (
                <Box>
                  <Button
                    onClick={handleOpenCsvExport}
                    startIcon={<RiDownload2Line />}
                    variant="primary"
                  >
                    CSV出力
                  </Button>
                </Box>
              )}
            </Flex>
          </>
        )}
        <DealsCsvExportModal
          handleClose={handleCloseCsvExport}
          isOpen={csvExportModalOpen}
          requestCsvGenerate={requestCsvGenerate}
          searchParams={dealSearchCondition}
        />
        <div className={Styles.SearchDetailContainer}>
          <Drawer
            bottomArea={renderSearchButton(handleDetailSearch)}
            isOpen={isOpenSearchDetailTab}
            onClickBackdrop={handleCloseSearchDetailTab}
            onClickCloseButton={handleCloseSearchDetailTab}
            title="詳細検索"
          >
            <DealSearchDetailTab
              dealSearchCondition={dealSearchCondition}
              handleChangeMaintenanceCompanyInput={
                handleChangeMaintenanceCompanyInput
              }
              maintenanceCompanies={maintenanceCompanies || null}
            />
          </Drawer>
        </div>
      </FormProvider>
    </div>
  );
};
